import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import certificateTemplateService from '@/services/certificate-template.service';
import { CertificateTemplateDto, CERTIFICATE_TEMPLATE_TYPE_LABELS } from '@/types/certificate-template.types';
import { invalidateTemplateQueries } from './templateQueries';

interface RestoreTemplateModalProps {
  template: CertificateTemplateDto | null;
  onClose: () => void;
  onRestored?: () => void; // e.g. step back a page when the last Trash item on it was restored
}

/** Restore from Trash. Comes back as a draft (not the default); a new name is asked only if the old one is taken. */
export default function RestoreTemplateModal({ template, onClose, onRestored }: RestoreTemplateModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [nameTaken, setNameTaken] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(template?.template_name || '');
    setNameTaken(false);
    setError('');
  }, [template]);

  const restoreMutation = useMutation({
    mutationFn: () => certificateTemplateService.restoreTemplate(template!._id, nameTaken ? name.trim() : undefined),
    onSuccess: (restored) => {
      toast.success(`"${restored.template_name}" restored. It is not the default until you click "Use this".`);
      // Comes back as a draft: the default does not change
      invalidateTemplateQueries(queryClient, template?.template_type, { inUse: false, deletedCount: true });
      onRestored?.();
      onClose();
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'duplicate_template_name' || /already exists/i.test(err.message)) {
        setNameTaken(true);
        setError('Another template already uses this name. We suggested a new one; you can change it.');
        // Offer a free name like "Gold (2)" so the admin does not have to invent one
        certificateTemplateService.suggestFreeTemplateName(template!.template_name)
          .then(suggested => setName(current => current.trim() === template!.template_name.trim() ? suggested : current))
          .catch(() => setError('Another template already uses this name. Enter a new name to restore it.'));
      } else {
        toast.error(err.message || 'Failed to restore template');
      }
    }
  });

  if (!template) return null;
  const isSaving = restoreMutation.isPending;

  const handleRestore = () => {
    if (nameTaken && name.trim().length < 3) {
      setError('Template name must be at least 3 characters.');
      return;
    }
    if (nameTaken && name.trim() === template.template_name.trim()) {
      setError('This name is already used. Please enter a different name.');
      return;
    }
    restoreMutation.mutate();
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleRestore}
        disabled={isSaving}
        className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
      >
        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        Restore
      </button>
    </div>
  );

  return (
    <Modal isOpen={!!template} onClose={() => !isSaving && onClose()} title="Restore template" size="md" footer={footer} closeOnOverlayClick={!isSaving}>
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Restore <span className="font-semibold">"{template.template_name}"</span> ({CERTIFICATE_TEMPLATE_TYPE_LABELS[template.template_type]})?
          It does not become the <span className="font-medium">Default</span>, so learners keep getting the current design until you click <span className="font-medium">Use this</span>.
        </p>
        {nameTaken && (
          <div>
            <label htmlFor="restore-template-name" className="block text-sm font-medium text-gray-900 mb-1">New name</label>
            <input
              id="restore-template-name"
              type="text"
              value={name}
              maxLength={100}
              autoFocus
              onChange={e => { setName(e.target.value); setError(''); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
